import { AssetRiskMetrics, AggregateRiskMetrics } from './types';

/**
 * Check if a point is inside a polygon using the ray casting algorithm
 * Adapted from InspectionLayer.tsx
 */
export const isPointInPolygon = (
  point: { lng: number; lat: number },
  polygon: number[][][]
): boolean => {
  const x = point.lng;
  const y = point.lat;
  const poly = polygon[0];
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i][0];
    const yi = poly[i][1];
    const xj = poly[j][0];
    const yj = poly[j][1];
    const intersect =
      yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
};

/**
 * Check if any part of an asset's footprint intersects with an ignition's final frame
 */
export const doesIgnitionImpactAsset = (
  asset: GeoJSON.Feature,
  ignitionFinalFrame: any
): boolean => {
  // Handle FeatureCollection structure
  if (ignitionFinalFrame?.type === 'FeatureCollection') {
    if (!ignitionFinalFrame.features || ignitionFinalFrame.features.length === 0) {
      return false;
    }
    
    // Check intersection with any feature in the collection
    return ignitionFinalFrame.features.some((feature: any) => 
      doesIgnitionImpactAssetWithGeometry(asset, feature)
    );
  }
  
  // Handle single Feature structure (fallback)
  return doesIgnitionImpactAssetWithGeometry(asset, ignitionFinalFrame);
};

/**
 * Helper function to check intersection with a single geometry feature
 */
const doesIgnitionImpactAssetWithGeometry = (
  asset: GeoJSON.Feature,
  ignitionFeature: any
): boolean => {
  if (!ignitionFeature?.geometry?.coordinates) {
    return false;
  }

  const assetGeometry = asset.geometry;
  const ignitionGeometry = ignitionFeature.geometry;
  
  // Handle different asset geometry types
  if (assetGeometry.type === 'Point') {
    const point = {
      lng: assetGeometry.coordinates[0],
      lat: assetGeometry.coordinates[1]
    };
    
    if (ignitionGeometry.type === 'Polygon') {
      return isPointInPolygon(point, ignitionGeometry.coordinates);
    }
  } else if (assetGeometry.type === 'Polygon' && ignitionGeometry.type === 'Polygon') {
    // Check if any point of the asset polygon is within the ignition geometry
    const assetCoords = assetGeometry.coordinates[0];
    for (const coord of assetCoords) {
      const point = { lng: coord[0], lat: coord[1] };
      if (isPointInPolygon(point, ignitionGeometry.coordinates)) {
        return true;
      }
    }
    
    // Also check if any point of the ignition geometry is within the asset
    const ignitionCoords = ignitionGeometry.coordinates[0];
    for (const coord of ignitionCoords) {
      const point = { lng: coord[0], lat: coord[1] };
      if (isPointInPolygon(point, assetGeometry.coordinates)) {
        return true;
      }
    }
  }
  
  return false;
};

/**
 * Calculate risk metrics for a single simulation
 */
export const calculateSimulationRiskMetrics = (
  asset: GeoJSON.Feature,
  simulation: any
): AssetRiskMetrics => {
  const ignitions = simulation.ignitions || [];
  const totalIgnitions = ignitions.length;

  // Prefer the backend-precomputed impacted asset ids: they are computed at
  // full resolution (including urban-spread ignitions) and are always present
  // in the results, whereas final_animation_frame is stripped from ensemble
  // results. The client-side geometry test remains as a fallback for results
  // that predate the annotation.
  const assetId = Number(asset?.properties?.id);
  const impactingIgnitions = ignitions.filter((ignition: any) => {
    if (Array.isArray(ignition.impacted_asset_ids) && !Number.isNaN(assetId)) {
      return ignition.impacted_asset_ids.includes(assetId);
    }
    return ignition.final_animation_frame &&
           doesIgnitionImpactAsset(asset, ignition.final_animation_frame);
  }).length;
  
  const impactPercentage = totalIgnitions > 0 ? (impactingIgnitions / totalIgnitions) * 100 : 0;
  
  return {
    simulationId: simulation.id || simulation.simulation_id,
    simulationName: simulation.name || `Simulation ${simulation.id}`,
    totalIgnitions,
    impactingIgnitions,
    impactPercentage: Math.round(impactPercentage * 100) / 100 // Round to 2 decimal places
  };
};

/**
 * Calculate aggregate risk metrics across multiple simulations
 */
export const calculateAggregateRiskMetrics = (
  asset: GeoJSON.Feature,
  simulations: any[]
): AggregateRiskMetrics => {
  const simulationMetrics = simulations.map(sim => 
    calculateSimulationRiskMetrics(asset, sim)
  );
  const totalSimulations = simulationMetrics.length;
  const totalIgnitions = simulationMetrics.reduce((sum, metrics) => sum + metrics.totalIgnitions, 0);
  const totalImpactingIgnitions = simulationMetrics.reduce((sum, metrics) => sum + metrics.impactingIgnitions, 0);
  
  const averageImpactPercentage = totalSimulations > 0 
    ? simulationMetrics.reduce((sum, metrics) => sum + metrics.impactPercentage, 0) / totalSimulations
    : 0;
  
  return {
    totalSimulations,
    totalIgnitions,
    totalImpactingIgnitions,
    averageImpactPercentage: Math.round(averageImpactPercentage * 100) / 100,
    simulationMetrics
  };
};
